import { useState } from 'react';
import { Navigation } from '../../components/Navigation';
import { FloatingBackButton } from '../../components/FloatingBackButton';
import { useAuthStore } from '../../store/authStore';
import { savePersonalityAssessment, calculatePersonalityTraits } from '../../lib/ai/matchingEngine';
import { useNavigate } from 'react-router-dom';
import { Brain, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';

interface Question {
  id: string;
  text: string;
  category: string;
}

const QUESTIONS: Question[] = [
  // Openness
  { id: 'creative', text: 'I enjoy thinking of new and creative solutions', category: 'openness' },
  { id: 'curious', text: 'I am curious about how things work', category: 'openness' },
  { id: 'imaginative', text: 'I have a vivid imagination', category: 'openness' },
  { id: 'artistic', text: 'I appreciate art and beauty', category: 'openness' },

  // Conscientiousness
  { id: 'organized', text: 'I keep my workspace organized', category: 'conscientiousness' },
  { id: 'reliable', text: 'People can count on me to meet deadlines', category: 'conscientiousness' },
  { id: 'thorough', text: 'I pay attention to details', category: 'conscientiousness' },
  { id: 'disciplined', text: 'I stick to my plans and schedules', category: 'conscientiousness' },

  // Extraversion
  { id: 'outgoing', text: 'I enjoy meeting new people', category: 'extraversion' },
  { id: 'energetic', text: 'I feel energized by social interactions', category: 'extraversion' },
  { id: 'social', text: 'I prefer working with others over working alone', category: 'extraversion' },
  { id: 'assertive', text: 'I am comfortable taking charge in group settings', category: 'extraversion' },

  // Agreeableness
  { id: 'friendly', text: 'I am considerate and kind to others', category: 'agreeableness' },
  { id: 'compassionate', text: 'I empathize with others\' feelings', category: 'agreeableness' },
  { id: 'cooperative', text: 'I work well in team environments', category: 'agreeableness' },
  { id: 'trustworthy', text: 'People trust me with confidential information', category: 'agreeableness' },

  // Neuroticism (Emotional Stability)
  { id: 'anxious', text: 'I often feel worried or anxious', category: 'neuroticism' },
  { id: 'moody', text: 'My mood changes frequently', category: 'neuroticism' },
  { id: 'stressed', text: 'I get stressed easily', category: 'neuroticism' },
  { id: 'worried', text: 'I worry about things that might go wrong', category: 'neuroticism' },

  // Work Style
  { id: 'independence', text: 'I prefer to work independently', category: 'work_style' },
  { id: 'collaboration', text: 'I thrive in collaborative environments', category: 'work_style' },
  { id: 'structure', text: 'I prefer structured work environments', category: 'work_style' },
  { id: 'flexibility', text: 'I adapt easily to changing priorities', category: 'work_style' },
  { id: 'innovation', text: 'I enjoy innovating and trying new approaches', category: 'work_style' },
  { id: 'stability', text: 'I value stability and predictability', category: 'work_style' },
];

const WORK_VALUES = [
  { id: 'growth', label: 'Career Growth', description: 'Opportunities for advancement and development' },
  { id: 'impact', label: 'Impact', description: 'Making a meaningful difference' },
  { id: 'autonomy', label: 'Autonomy', description: 'Independence and self-direction' },
  { id: 'teamwork', label: 'Teamwork', description: 'Collaboration and camaraderie' },
  { id: 'innovation', label: 'Innovation', description: 'Cutting-edge work and creativity' },
  { id: 'stability', label: 'Stability', description: 'Job security and consistency' },
  { id: 'compensation', label: 'Compensation', description: 'Competitive salary and benefits' },
  { id: 'work_life_balance', label: 'Work-Life Balance', description: 'Flexibility and time off' },
];

export function PersonalityAssessment() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<Record<string, number>>({});
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [communicationPreferences, setCommunicationPreferences] = useState<string[]>([]);
  const [meetingFrequency, setMeetingFrequency] = useState<string>('moderate');
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  const totalSteps = 3; // Questions, Values, Communication

  const handleResponse = (questionId: string, value: number) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const toggleValue = (valueId: string) => {
    setSelectedValues(prev =>
      prev.includes(valueId)
        ? prev.filter(v => v !== valueId)
        : [...prev, valueId]
    );
  };

  const toggleCommunication = (pref: string) => {
    setCommunicationPreferences(prev =>
      prev.includes(pref)
        ? prev.filter(p => p !== pref)
        : [...prev, pref]
    );
  };

  const handleSubmit = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Calculate personality traits from responses
      const traits = calculatePersonalityTraits(responses);

      // Calculate work style scores
      const workStyle = {
        independence: Math.round((responses['independence'] || 3) * 20),
        collaboration: Math.round((responses['collaboration'] || 3) * 20),
        structure: Math.round((responses['structure'] || 3) * 20),
        flexibility: Math.round((responses['flexibility'] || 3) * 20),
        innovation: Math.round((responses['innovation'] || 3) * 20),
        stability: Math.round((responses['stability'] || 3) * 20),
      };

      // Save assessment
      const result = await savePersonalityAssessment(user.id, {
        ...traits,
        work_style: workStyle,
        communication_preference: communicationPreferences,
        meeting_frequency_preference: meetingFrequency,
        core_values: selectedValues,
        motivators: {}, // Could expand this with weighted values
      });

      if (result) {
        setCompleted(true);
        setTimeout(() => {
          navigate('/jobseeker/gamification');
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (currentStep === 0) {
      // Check if all questions answered
      return QUESTIONS.every(q => responses[q.id] !== undefined);
    } else if (currentStep === 1) {
      // Check if at least 3 values selected
      return selectedValues.length >= 3;
    } else if (currentStep === 2) {
      // Check if communication preferences selected
      return communicationPreferences.length > 0;
    }
    return false;
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Assessment Complete!</h2>
          <p className="text-gray-600 mb-4">Your personality profile has been saved.</p>
          <p className="text-sm text-gray-500">Redirecting you to see your enhanced matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <FloatingBackButton />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center mx-auto mb-4">
            <Brain className="w-9 h-9 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Personality Assessment</h1>
          <p className="text-gray-600">Help us find your perfect culture fit</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Step {currentStep + 1} of {totalSteps}</span>
            <span>{Math.round(((currentStep + 1) / totalSteps) * 100)}% Complete</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-600 to-blue-600 transition-all duration-300"
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-8">
          {currentStep === 0 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">How do you work best?</h2>
              <p className="text-gray-600 mb-6">
                Rate each statement from 1 (Strongly Disagree) to 5 (Strongly Agree)
              </p>

              {QUESTIONS.map(question => (
                <div key={question.id} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-900">
                    {question.text}
                  </label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map(value => (
                      <button
                        key={value}
                        onClick={() => handleResponse(question.id, value)}
                        className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                          responses[question.id] === value
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                  {value === 1 && <span className="text-xs text-gray-500">Strongly Disagree</span>}
                  {value === 5 && <span className="text-xs text-gray-500 text-right block">Strongly Agree</span>}
                </div>
              ))}
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">What matters most to you?</h2>
              <p className="text-gray-600 mb-6">Select your top 3-5 work values</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {WORK_VALUES.map(value => (
                  <button
                    key={value.id}
                    onClick={() => toggleValue(value.id)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      selectedValues.includes(value.id)
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        selectedValues.includes(value.id)
                          ? 'border-purple-600 bg-purple-600'
                          : 'border-gray-300'
                      }`}>
                        {selectedValues.includes(value.id) && (
                          <CheckCircle className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className="font-bold text-gray-900">{value.label}</span>
                    </div>
                    <p className="text-sm text-gray-600">{value.description}</p>
                  </button>
                ))}
              </div>

              <p className="text-sm text-gray-500 text-center">
                Selected: {selectedValues.length} / {WORK_VALUES.length}
              </p>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Communication Preferences</h2>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  How do you prefer to communicate?
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {['Written', 'Verbal', 'Visual', 'Async', 'Realtime', 'Video Calls'].map(pref => (
                    <button
                      key={pref}
                      onClick={() => toggleCommunication(pref.toLowerCase())}
                      className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                        communicationPreferences.includes(pref.toLowerCase())
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-200 text-gray-700 hover:border-purple-300'
                      }`}
                    >
                      {pref}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Preferred meeting frequency
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'minimal', label: 'Minimal' },
                    { value: 'moderate', label: 'Moderate' },
                    { value: 'frequent', label: 'Frequent' },
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setMeetingFrequency(option.value)}
                      className={`py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                        meetingFrequency === option.value
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-200 text-gray-700 hover:border-purple-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-6 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>

          <button
            onClick={handleNext}
            disabled={!canProceed() || loading}
            className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : currentStep === totalSteps - 1 ? (
              <>
                Complete
                <CheckCircle className="w-5 h-5" />
              </>
            ) : (
              <>
                Next
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </main>
    </div>
  );
}
